import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { differenceInCalendarDays } from "date-fns";
import { todayAnalysisRange } from "../../utils/helpers/applicationDate";
import { connect } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import {
  clearSoldProductDetail,
  fetchDashboardProfitDetails,
  fetchDashboardMaterialUsage,
  fetchDashboardOverview,
  fetchSoldProductDetail,
} from "../../actions/dashboardAction";
import { formatIDR } from "../../utils/helpers/formatIDR";
import ChartState from "./components/ChartState";
import KpiCard from "./components/KpiCard";
import TopSellingProducts from "./components/TopSellingProducts";
import TopCashiers from "./components/TopCashiers";
import CashierSummaryModal from "../SalePage/CashierSummaryModal";
import ProfitDetailModal from "./components/ProfitDetailModal";
import { dashboardRangeLabel, validDashboardRange } from "./components/ownerDashboard";
import { hasAnyPermission } from "../../utils/helpers/permissions";
import SoldProductDetailModal from "./components/SoldProductDetailModal";
import ProductAnalysis from "./components/MenuProfitAnalysis";
import MaterialUsageChart from "./components/MaterialUsageChart";
import UserAvatar from "../../components/UserAvatar";
import ItemAvatar from "../../components/ItemAvatar";
import "./dashboard.css";
import { getStoreName } from "../../utils/helpers/storeSetting";
import useAkasChartColors from "../../hooks/useAkasChartColors";
import PATH_URL from "../../routers/path";

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const DASHBOARD_SCOPES = [
  {
    value: "overview",
    label: "Ringkasan",
    title: "Gambaran utama bisnis",
    description: "Angka penting dan kondisi usaha dalam satu tampilan ringkas.",
  },
  // { value: "sales", label: "Penjualan", title: "Performa penjualan", description: "Pantau transaksi, produk terjual, pembayaran, dan pelanggan loyal." },
  {
    value: "inventory",
    label: "Material & Stok",
    title: "Kesehatan material dan stok",
    description: "Temukan material yang paling banyak digunakan dan stok kritis sebelum mengganggu operasional.",
  },
  // { value: "purchases", label: "Pembelian", title: "Kontrol pembelian", description: "Pantau nilai pembelian serta perubahan biaya bahan dan dampaknya ke HPP." },
  {
    value: "products",
    label: "Produk",
    title: "Performa produk",
    description: "Lihat penjualan dan keuntungan setiap menu.",
  },
  {
    value: "finance",
    label: "Keuangan",
    title: "Ringkasan keuangan",
    description: "Lihat penagihan, piutang, biaya, dan profitabilitas periode terpilih.",
  },
];

const DASHBOARD_SCOPE_VALUES = new Set(DASHBOARD_SCOPES.map((scope) => scope.value));
const PAYMENT_METHOD_LABELS = {
  cash: "Tunai",
  qris: "QRIS",
  transfer: "Transfer",
  credit: "Tempo",
  mixed: "Gabungan",
};

const compactNumber = (value) =>
  new Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(Number(value || 0));

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;

const formatUnitPrice = (value, unitLabel) => `${formatIDR(value)}/${unitLabel || "unit"}`;

const getCostAlertMeta = (alert = {}) => {
  const isPriceAlert = alert.type === "price_spike";
  const change = Number(alert.change_percent || 0);

  return {
    isPriceAlert,
    icon: change >= 0 ? "↑" : "↓",
    title: isPriceAlert ? "Harga Material" : "Pembelian Tidak Normal",
    directionLabel: change >= 0 ? "Naik" : "Turun",
    toneClass: change >= 0 ? "dashboard-cost-alert-danger" : "dashboard-cost-alert-success",
  };
};

const DashboardIcon = ({ name }) => {
  const paths = {
    revenue: (
      <>
        <path d="M3 6h18v12H3z" />
        <path d="M7 10h.01M17 14h.01" />
        <circle cx="12" cy="12" r="2" />
      </>
    ),
    cogs: (
      <>
        <path d="M4 7l8-4l8 4v10l-8 4l-8-4z" />
        <path d="M4 7l8 4l8-4M12 11v10" />
      </>
    ),
    profit: (
      <>
        <path d="M4 19V9M10 19V5M16 19v-7M22 19H2" />
        <path d="M4 9l6-4l6 7l5-6" />
      </>
    ),
    margin: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 16l8-8M9 8h.01M15 16h.01" />
      </>
    ),
    transaction: (
      <>
        <path d="M5 3h14v18l-3-2l-2 2l-2-2l-2 2l-2-2l-3 2z" />
        <path d="M9 8h6M9 12h6" />
      </>
    ),
    average: (
      <>
        <path d="M4 19V5M4 19h16" />
        <path d="M7 15l4-4l3 2l5-6" />
      </>
    ),
    purchase: (
      <>
        <path d="M5 3h14v18l-3-2l-2 2l-2-2l-2 2l-2-2l-3 2z" />
        <path d="M9 8h6M9 12h6M9 16h3" />
      </>
    ),
    cashflow: (
      <>
        <path d="M4 8h16M4 16h16" />
        <path d="M8 4l-4 4l4 4M16 12l4 4l-4 4" />
      </>
    ),
    expense: (
      <>
        <path d="M5 3h14v18l-3-2l-2 2l-2-2l-2 2l-2-2l-3 2z" />
        <path d="M9 8h6M9 12h6M9 16h3" />
      </>
    ),
    net: (
      <>
        <path d="M4 19V5M4 19h16" />
        <path d="M7 14l3-3l3 2l5-6" />
        <path d="M17 7h1v1" />
      </>
    ),
  };

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

const DashboardPage = ({
  overview,
  isLoading,
  userProfile,
  profitDetail,
  profitDetailLoading,
  profitDetailError,
  fetchDashboardProfitDetails,
  error,
  storeSetting,
  soldProductDetail,
  soldProductDetailLoading,
  soldProductDetailError,
  materialUsage,
  materialUsageLoading,
  materialUsageError,
  fetchDashboardOverview,
  fetchDashboardMaterialUsage,
  fetchSoldProductDetail,
  clearSoldProductDetail,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState(todayAnalysisRange);
  const [dateInputs, setDateInputs] = useState(() => ({ ...dateRange }));
  const [dateError, setDateError] = useState("");
  const [profitDetailType, setProfitDetailType] = useState(null);
  const [profitDetailPage, setProfitDetailPage] = useState(1);
  const [criticalStockType, setCriticalStockType] = useState("material");
  const [selectedSoldProduct, setSelectedSoldProduct] = useState(null);
  const [materialUsageFilters, setMaterialUsageFilters] = useState({
    category: "all",
    unit: "all",
    groupBy: "material",
    drillDownCategory: "",
  });
  const chartColors = useAkasChartColors();
  const meta = overview?.meta || EMPTY_OBJECT;
  const updatedAt = meta.generated_at ? new Date(meta.generated_at) : null;
  const updatedLabel =
    updatedAt && !Number.isNaN(updatedAt.getTime())
      ? new Intl.DateTimeFormat("id-ID", {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }).format(updatedAt)
      : "Belum diperbarui";
  const summary = overview?.summary || EMPTY_OBJECT;
  const trend = overview?.trend || EMPTY_OBJECT;
  const inventory = overview?.inventory || EMPTY_OBJECT;
  const paymentMethods = overview?.payment_methods || EMPTY_ARRAY;
  const topProducts = overview?.top_products || EMPTY_ARRAY;
  const customerLoyalty = overview?.customer_loyalty || EMPTY_OBJECT;
  const topRepeatCustomers = customerLoyalty?.top_repeat_customers || EMPTY_ARRAY;
  const costAlerts = overview?.cost_alerts || EMPTY_ARRAY;
  const highestCostAlert = costAlerts?.[0] || null;
  const errorMessage = error;
  const requestedScope = searchParams.get("scope");
  const activeScope = DASHBOARD_SCOPE_VALUES.has(requestedScope) ? requestedScope : "overview";
  const activeScopeMeta = DASHBOARD_SCOPES.find((scope) => scope.value === activeScope) || DASHBOARD_SCOPES[0];
  const productSoldTotal = useMemo(
    () => topProducts.reduce((total, item) => total + Number(item?.total_qty || 0), 0),
    [topProducts],
  );
  const newCustomersTotal =
    summary.new_customers ||
    summary.new_customer ||
    summary.new_clients ||
    summary.customers_new ||
    summary.total_new_customers ||
    0;
  const totalProductsSold = Number(summary.products_sold || summary.total_products_sold || productSoldTotal || 0);
  const revenue = Number(summary.revenue || 0);
  const revenueCollected = Number(summary.revenue_collected || 0);
  const collectionRate = revenue > 0 ? (revenueCollected / revenue) * 100 : 0;
  const grossMargin = Number(summary.gross_margin || 0);
  const topProductShare =
    totalProductsSold > 0 ? (Number(topProducts?.[0]?.total_qty || 0) / totalProductsSold) * 100 : 0;
  const totalCustomers = Number(summary.total_customers || 0);
  const newCustomerRate = totalCustomers > 0 ? (Number(newCustomersTotal || 0) / totalCustomers) * 100 : 0;
  const transactionCount = Number(summary.transactions || summary.total_transactions || summary.transaction_count || 0);
  const averageTransaction =
    transactionCount > 0 ? revenue / transactionCount : Number(summary.average_transaction || 0);
  const criticalItemsCount = Number(inventory.status?.low_stock || 0) + Number(inventory.status?.out_of_stock || 0);
  const totalInventoryItems = Number(inventory.total_items || 0);
  const stockSafeRate =
    totalInventoryItems > 0 ? Math.max(0, ((totalInventoryItems - criticalItemsCount) / totalInventoryItems) * 100) : 0;
  const inventoryStatus = inventory.status || EMPTY_OBJECT;
  const activeCustomerCount = Number(customerLoyalty?.active_customer_count || 0);
  const repeatCustomerCount = Number(customerLoyalty?.repeat_customer_count || 0);
  const repeatCustomerRate = Number(customerLoyalty?.repeat_rate || 0);
  const purchaseTotal = Number(summary.purchases || 0);
  const operatingExpenses = Number(summary.operating_expenses || 0);
  const grossProfit = Number(summary.gross_profit || 0);
  const netProfit = Number(summary.net_profit || 0);
  const netMargin = Number(summary.net_margin || 0);
  const revenueReceivables = Number(summary.receivables || 0);
  const purchasePeak = Math.max(...(trend.purchases || []).map((value) => Number(value || 0)), 0);
  const purchasePeakShare = purchaseTotal > 0 ? (purchasePeak / purchaseTotal) * 100 : 0;
  const operatingExpenseRate = revenue > 0 ? (operatingExpenses / revenue) * 100 : 0;
  const groupBy = useMemo(() => {
    const diff = differenceInCalendarDays(
      new Date(`${dateRange.endDate}T00:00:00`),
      new Date(`${dateRange.startDate}T00:00:00`),
    );

    return diff > 90 ? "month" : "day";
  }, [dateRange.endDate, dateRange.startDate]);
  const rangeLabel = dashboardRangeLabel(dateRange.startDate, dateRange.endDate);
  const isSingleDay = dateRange.startDate === dateRange.endDate;
  const pulseData = useMemo(() => {
    const values = (trend.revenue || []).slice(-7).map((value) => Number(value || 0));
    const labels = (trend.labels || []).slice(-7);
    const maxValue = Math.max(...values, 0);

    return values.map((value, index) => ({
      value,
      label: labels[index] || `H${index + 1}`,
      height: maxValue > 0 ? Math.max(10, (value / maxValue) * 100) : 10,
    }));
  }, [trend.labels, trend.revenue]);
  const filteredCriticalItems = useMemo(
    () => (inventory.critical_items || []).filter((item) => item.item_type === criticalStockType),
    [inventory.critical_items, criticalStockType],
  );

  useEffect(() => {
    fetchDashboardOverview(dateRange.startDate, dateRange.endDate, groupBy);
  }, [dateRange.endDate, dateRange.startDate, fetchDashboardOverview, groupBy]);

  useEffect(() => {
    if (!validDashboardRange(dateInputs)) {
      setDateError("Isi tanggal awal dan akhir. Tanggal akhir tidak boleh sebelum tanggal awal.");
      return undefined;
    }

    setDateError("");
    if (dateInputs.startDate === dateRange.startDate && dateInputs.endDate === dateRange.endDate) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setDateRange({ ...dateInputs });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [dateInputs, dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (activeScope !== "inventory") return;

    fetchDashboardMaterialUsage({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...materialUsageFilters,
    });
  }, [activeScope, dateRange.endDate, dateRange.startDate, fetchDashboardMaterialUsage, materialUsageFilters]);

  const openProfitDetail = (type) => {
    fetchDashboardProfitDetails(type, dateRange.startDate, dateRange.endDate, 1);
    setProfitDetailPage(1);
    setProfitDetailType(type);
  };
  const changeProfitDetailPage = (page) => {
    fetchDashboardProfitDetails(profitDetailType, dateRange.startDate, dateRange.endDate, page);
    setProfitDetailPage(page);
  };
  const closeProfitDetail = useCallback(() => setProfitDetailType(null), []);
  const handleMaterialUsageFilterChange = (name, value) => {
    setMaterialUsageFilters((current) => ({
      ...current,
      [name]: value,
      drillDownCategory: "",
    }));
  };

  const handleScopeChange = (scope) => {
    const nextParams = new URLSearchParams(searchParams);

    if (scope === "overview") nextParams.delete("scope");
    else nextParams.set("scope", scope);

    setSearchParams(nextParams, { replace: true });
  };

  const openSoldProductDetail = (product) => {
    setSelectedSoldProduct(product);
    clearSoldProductDetail();
    fetchSoldProductDetail(product.id, dateRange.startDate, dateRange.endDate);
  };

  const closeSoldProductDetail = () => {
    setSelectedSoldProduct(null);
    clearSoldProductDetail();
  };

  useEffect(() => {
    setSelectedSoldProduct(null);
    clearSoldProductDetail();
  }, [dateRange.startDate, dateRange.endDate, activeScope, clearSoldProductDetail]);

  const scopeKpis =
    {
      sales: [
        {
          title: "Total Penjualan",
          value: formatIDR(revenue),
          color: "success",
          icon: "revenue",
          progress: collectionRate,
          progressLabel: "Tertagih",
        },
        {
          title: "Jumlah Transaksi",
          value: transactionCount.toLocaleString("id-ID"),
          subtitle: "transaksi",
          color: "primary",
          icon: "transaction",
          footnote: rangeLabel,
        },
        {
          title: "Rata-rata Transaksi",
          value: formatIDR(Number(summary.average_order_value || averageTransaction)),
          color: "cyan",
          icon: "average",
          footnote: "Nilai rata-rata setiap transaksi",
        },
        {
          title: "Produk Terjual",
          value: totalProductsSold.toLocaleString("id-ID"),
          subtitle: "item",
          color: "success",
          icon: "profit",
          progress: topProductShare,
          progressLabel: "Kontribusi menu teratas",
        },
      ],
      inventory: [
        {
          title: "Total Material & Stok",
          value: totalInventoryItems.toLocaleString("id-ID"),
          subtitle: "inventori outlet aktif",
          color: "primary",
          icon: "cogs",
          footnote: "Inventori pada outlet aktif",
        },
        {
          title: "Stok Sehat",
          value: Number(inventoryStatus.healthy || 0).toLocaleString("id-ID"),
          subtitle: "item",
          color: "success",
          icon: "profit",
          progress: stockSafeRate,
          progressLabel: "Dari total inventori",
        },
        {
          title: "Stok Rendah",
          value: Number(inventoryStatus.low_stock || 0).toLocaleString("id-ID"),
          subtitle: "item",
          color: "warning",
          icon: "expense",
          footnote: "Sudah menyentuh stok minimum",
        },
        {
          title: "Stok Habis",
          value: Number(inventoryStatus.out_of_stock || 0).toLocaleString("id-ID"),
          subtitle: "item",
          color: "danger",
          icon: "transaction",
          footnote: "Perlu segera ditindaklanjuti",
        },
      ],
      purchases: [
        {
          title: "Total Pembelian",
          value: formatIDR(purchaseTotal),
          color: "primary",
          icon: "purchase",
          footnote: rangeLabel,
        },
        {
          title: "Biaya Operasional",
          value: formatIDR(operatingExpenses),
          color: "danger",
          icon: "expense",
          footnote: `${formatPercent(operatingExpenseRate)} dari total penjualan · termasuk penyusutan`,
        },
        {
          title: "Puncak Pembelian",
          value: formatIDR(purchasePeak),
          color: "cyan",
          icon: "average",
          progress: purchasePeakShare,
          progressLabel: "Dari total periode",
        },
        {
          title: "Cost & HPP Alert",
          value: costAlerts.length.toLocaleString("id-ID"),
          subtitle: "peringatan",
          color: costAlerts.length ? "warning" : "success",
          icon: "cogs",
          footnote: costAlerts.length ? "Perubahan biaya perlu diperiksa" : "Biaya pembelian masih normal",
        },
      ],
      finance: [
        {
          title: "Kas Bersih Penjualan",
          value: summary.sales_cash_net == null ? "—" : formatIDR(summary.sales_cash_net),
          color: "success",
          icon: "cashflow",
          footnote: "Penerimaan dikurangi pengembalian, sesuai tanggal mutasi",
        },
        {
          title: "Piutang Penjualan",
          value: formatIDR(revenueReceivables),
          color: revenueReceivables > 0 ? "warning" : "success",
          icon: "transaction",
          footnote:
            revenueReceivables > 0 ? "Sisa tagihan dari penjualan periode ini" : "Penjualan periode ini sudah tertagih",
        },
        {
          title: "Laba Kotor",
          value: formatIDR(grossProfit),
          color: grossProfit >= 0 ? "success" : "danger",
          icon: "profit",
          progress: grossMargin,
          progressLabel: "Margin kotor",
        },
        {
          title: "Laba Bersih Tercatat",
          value: formatIDR(netProfit),
          color: netProfit >= 0 ? "primary" : "danger",
          icon: "net",
          progress: netMargin,
          progressLabel: "Margin bersih",
        },
      ],
    }[activeScope] || EMPTY_ARRAY;

  const scopeTrend =
    {
      overview: {
        title: "Tren Penjualan & Biaya",
        description: "Total penjualan, pembelian, dan biaya operasional pada periode terpilih.",
        series: [
          { name: "Total Penjualan", data: trend.revenue || [] },
          { name: "Pembelian", data: trend.purchases || [] },
          { name: "Biaya Operasional", data: trend.operating_expenses || [] },
        ],
        values: [
          { label: "Total Penjualan", value: revenue, tone: "money-in" },
          { label: "Pembelian", value: purchaseTotal, tone: "money-out" },
          { label: "Biaya Operasional", value: operatingExpenses, tone: "operating-expense" },
        ],
        colors: [chartColors.moneyIn, chartColors.moneyOut, chartColors.warning],
      },
      sales: {
        title: "Tren penjualan dan laba kotor",
        description: "Pergerakan total penjualan dan laba kotor dari transaksi yang valid.",
        series: [
          { name: "Total Penjualan", data: trend.revenue || [] },
          { name: "Laba kotor", data: trend.gross_profit || [] },
        ],
        values: [
          { label: "Total Penjualan", value: revenue, tone: "money-in" },
          { label: "Laba kotor", value: grossProfit, tone: "profit" },
        ],
        colors: [chartColors.moneyIn, chartColors.moneyInDeep],
      },
      purchases: {
        title: "Tren nilai pembelian",
        description: "Nilai pembelian bahan dan kebutuhan operasional pada periode terpilih.",
        series: [{ name: "Pembelian", data: trend.purchases || [] }],
        values: [{ label: "Total pembelian", value: purchaseTotal, tone: "money-out" }],
        colors: [chartColors.moneyOut],
      },
      finance: {
        title: "Penjualan, Pembelian & Biaya Operasional",
        description: "Total penjualan, pembelian, dan biaya operasional pada periode terpilih.",
        series: [
          { name: "Total Penjualan", data: trend.revenue || [] },
          { name: "Total Pembelian", data: trend.purchases || [] },
          { name: "Biaya Operasional", data: trend.operating_expenses || [] },
        ],
        values: [
          { label: "Total Penjualan", value: revenue, tone: "money-in" },
          { label: "Total Pembelian", value: purchaseTotal, tone: "money-out" },
          { label: "Biaya Operasional", value: operatingExpenses, tone: "operating-expense" },
        ],
        colors: [chartColors.moneyIn, chartColors.moneyOut, chartColors.warning],
      },
    }[activeScope] || null;

  const isFinanceChart = activeScope === "finance";
  const trendChartType = isFinanceChart ? "bar" : "area";
  const currencyTooltip = useMemo(() => ({ y: { formatter: (value) => formatIDR(value) } }), []);

  const trendOptions = useMemo(
    () => ({
      chart: {
        type: trendChartType,
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false },
        selection: { enabled: false },
        parentHeightOffset: 0,
        fontFamily: "inherit",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 520,
          dynamicAnimation: { enabled: true, speed: 320 },
        },
      },
      colors: scopeTrend?.colors || [chartColors.moneyIn, chartColors.moneyOut],
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 4,
          borderRadiusApplication: "end",
        },
      },
      stroke: {
        curve: "smooth",
        lineCap: "round",
        width: isFinanceChart ? 0 : (scopeTrend?.colors || []).map((_, index) => (index === 0 ? 3 : 2.5)),
      },
      markers: {
        size: 0,
        hover: { size: 0, sizeOffset: 0 },
      },
      fill: {
        type: isFinanceChart ? "solid" : "gradient",
        opacity: 1,
        gradient: {
          shadeIntensity: 0.25,
          opacityFrom: 0.3,
          opacityTo: 0.015,
          stops: [0, 72, 100],
        },
      },
      xaxis: {
        categories: trend.labels || [],
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: false },
        tooltip: { enabled: false },
        labels: {
          rotate: 0,
          trim: true,
          hideOverlappingLabels: true,
        },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          formatter: compactNumber,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        ...currencyTooltip,
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        markers: { width: 8, height: 8, radius: 8 },
      },
      grid: {
        borderColor: chartColors.grid,
        strokeDashArray: 3,
        xaxis: { lines: { show: false } },
        padding: { left: 8, right: 12, top: 4, bottom: 0 },
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
    }),
    [trend.labels, currencyTooltip, chartColors.grid, chartColors.moneyIn, chartColors.moneyOut, scopeTrend?.colors, isFinanceChart, trendChartType],
  );

  return (
    <div className="page-wrapper akas-dashboard-page">
      <div className="page-header d-print-none dashboard-header">
        <div className="container-xl">
          <div className="dashboard-welcome-panel">
            <div className="dashboard-welcome-copy">
              <div className="dashboard-live-label">
                <span /> Ringkasan bisnis terkini
              </div>
              <h2 className="page-title">
                Performa <em>{getStoreName(storeSetting)}</em>
              </h2>
              <div className="dashboard-quick-actions">
                {hasAnyPermission(userProfile, ["view-pos"]) ? (
                  <Link className="dashboard-primary-action" to={PATH_URL.POS}>
                    Buka kasir <span>↗</span>
                  </Link>
                ) : null}
                {hasAnyPermission(userProfile, ["view-stock-reports", "view-stock-opnames"]) ? (
                  <Link className="dashboard-secondary-action" to={PATH_URL.STOCK_REPORT}>
                    Laporan Stok <span>→</span>
                  </Link>
                ) : null}
              </div>
            </div>
            <div className="dashboard-period-panel d-print-none">
              <div className="dashboard-period-heading">
                <div>
                  <span>Periode analisis</span>
                  <strong>{rangeLabel}</strong>
                </div>
                <i aria-hidden="true">⌁</i>
              </div>
              <div>
                <div className="dashboard-date-fields">
                  <label>
                    <span>Dari tanggal</span>
                    <input
                      type="date"
                      className="form-control"
                      value={dateInputs.startDate}
                      aria-invalid={Boolean(dateError)}
                      aria-describedby={dateError ? "dashboard-date-error" : undefined}
                      onChange={(event) => setDateInputs({ ...dateInputs, startDate: event.target.value })}
                    />
                  </label>
                  <span className="dashboard-date-arrow">→</span>
                  <label>
                    <span>Sampai tanggal</span>
                    <input
                      type="date"
                      className="form-control"
                      value={dateInputs.endDate}
                      aria-invalid={Boolean(dateError)}
                      aria-describedby={dateError ? "dashboard-date-error" : undefined}
                      onChange={(event) => setDateInputs({ ...dateInputs, endDate: event.target.value })}
                    />
                  </label>
                </div>
                {dateError ? (
                  <p className="dashboard-date-error" id="dashboard-date-error" role="alert">
                    {dateError}
                  </p>
                ) : null}
              </div>
              {/* <div className="dashboard-period-foot">
                <span>
                  <i /> {isLoading ? "Memperbarui..." : `Diperbarui ${updatedLabel}`}
                </span>
                <button
                  type="button"
                  className="dashboard-refresh"
                  disabled={isLoading}
                  onClick={() => fetchDashboardOverview(dateRange.startDate, dateRange.endDate, groupBy)}
                  aria-label="Perbarui dashboard">
                  ↻
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body dashboard-page-body">
        <div className="container-xl">
          {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}

          <section className="dashboard-scope-toolbar d-print-none" aria-label="Lingkup dashboard">
            <div className="dashboard-scope-copy">
              <h3>{activeScopeMeta.title}</h3>
            </div>
            <div className="dashboard-scope-scroll">
              <div className="akas-scope-filter dashboard-scope-filter" role="group" aria-label="Pilih fokus dashboard">
                {DASHBOARD_SCOPES.map((scope) => (
                  <button
                    type="button"
                    key={scope.value}
                    className={`btn btn-sm ${activeScope === scope.value ? "btn-primary" : "btn-white"}`}
                    aria-pressed={activeScope === scope.value}
                    onClick={() => handleScopeChange(scope.value)}>
                    {scope.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {!errorMessage ? (
            <>
              {activeScope === "overview" ? (
                <div className="dashboard-pulse-layout dashboard-pulse-layout-single mb-4 dashboard-scope-content">
                  <section
                    className="dashboard-pulse-card"
                    aria-label={`${isSingleDay ? "Ringkasan hari ini" : "Ringkasan periode"} ${rangeLabel}`}>
                    <div className="dashboard-pulse-orbit" />
                    <div className="dashboard-pulse-top">
                      <div>
                        <span>{isSingleDay ? "Ringkasan hari ini" : "Ringkasan periode"}</span>
                        <small>{rangeLabel}</small>
                      </div>
                      <b>
                        <i /> Live
                      </b>
                    </div>
                    <div className="dashboard-pulse-amount">
                      <small>Total penjualan</small>
                      <strong>{isLoading ? "Memuat..." : formatIDR(revenue)}</strong>
                    </div>
                    <div className="dashboard-pulse-bars" aria-label="Tren penjualan terbaru">
                      {(pulseData.length
                        ? pulseData
                        : [...Array(7)].map((_, index) => ({ value: 0, label: `H${index + 1}`, height: 10 }))
                      ).map((item, index, collection) => (
                        <div
                          className="dashboard-pulse-bar"
                          key={`${item.label}-${index}`}
                          title={`${item.label}: ${formatIDR(item.value)}`}>
                          <i
                            className={index >= collection.length - 2 ? "is-highlight" : ""}
                            style={{ height: `${item.height}%` }}
                          />
                          <span>{item.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="dashboard-pulse-stats">
                      <div>
                        <strong>{transactionCount.toLocaleString("id-ID")}</strong>
                        <span>Transaksi</span>
                      </div>
                      <div>
                        <strong>{totalProductsSold.toLocaleString("id-ID")}</strong>
                        <span>Produk terjual</span>
                      </div>
                      <div>
                        <strong>{stockSafeRate.toFixed(0)}%</strong>
                        <span>Stok aman</span>
                      </div>
                      <div>
                        <strong>{formatIDR(averageTransaction)}</strong>
                        <span>Rata-rata transaksi</span>
                      </div>
                    </div>
                  </section>
                </div>
              ) : null}

              {activeScope !== "overview" && activeScope !== "products" ? (
                <div className="row row-cards dashboard-kpi-row mb-4 dashboard-scope-content">
                  {scopeKpis.map((kpi) => (
                    <KpiCard
                      key={`${activeScope}-${kpi.title}`}
                      title={kpi.title}
                      value={kpi.value}
                      subtitle={kpi.subtitle}
                      color={kpi.color}
                      icon={<DashboardIcon name={kpi.icon} />}
                      loading={isLoading}
                      progress={kpi.progress}
                      progressLabel={kpi.progressLabel}
                      footnote={kpi.footnote}
                      comparison={kpi.comparison}
                      onClick={kpi.onClick}
                    />
                  ))}
                </div>
              ) : null}

              <div className="row row-cards">
                {scopeTrend ? (
                  <div className="col-12 dashboard-scope-content">
                    <div className="card h-100 dashboard-card dashboard-chart-card dashboard-sales-summary">
                      <div className="card-header dashboard-trend-header">
                        <div>
                          <h3 className="card-title">{scopeTrend.title}</h3>
                        </div>
                        <div className="card-actions dashboard-trend-values d-none d-md-flex">
                          {scopeTrend.values.map((item) => (
                            <div className={`dashboard-legend-value dashboard-legend-${item.tone}`} key={item.label}>
                              <span>{item.label}</span>
                              <strong>{formatIDR(item.value)}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="card-body">
                        {activeScope === "overview" || isFinanceChart ? (
                          <div className="dashboard-trend-note">Biaya operasional termasuk penyusutan.</div>
                        ) : null}
                        <ChartState
                          loading={isLoading}
                          empty={
                            !scopeTrend.series.some((series) => series.data?.some((value) => Number(value) !== 0))
                          }>
                          <Chart key={trendChartType} options={trendOptions} series={scopeTrend.series} type={trendChartType} height={320} />
                        </ChartState>
                      </div>
                    </div>
                  </div>
                ) : null}

                {activeScope === "overview" ? (
                  <div className="col-lg-6 dashboard-scope-content">
                    <TopSellingProducts
                      products={topProducts}
                      loading={isLoading || Boolean(error)}
                      onSelect={openSoldProductDetail}
                    />
                  </div>
                ) : null}

                {activeScope === "overview" ? (
                  <div className="col-lg-6 dashboard-scope-content">
                    <TopCashiers cashiers={overview?.top_cashiers || EMPTY_ARRAY} loading={isLoading || Boolean(error)} />
                  </div>
                ) : null}

                {/* <div className="col-lg-6">
              <div className="card h-100 dashboard-card dashboard-chart-card">
                <div className="card-header">
                  <h3 className="card-title">Metode Pembayaran</h3>
                </div>
                <div className="card-body">
                  <ChartState loading={isLoading} empty={paymentMethods.length === 0}>
                    <Chart
                      options={paymentOptions}
                      series={paymentMethods.map((item) => item.total)}
                      type="donut"
                      height={330}
                    />
                  </ChartState>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card h-100 dashboard-card dashboard-chart-card">
                <div className="card-header">
                  <h3 className="card-title">Kesehatan Stok</h3>
                  <div className="card-actions">
                    <span className="badge bg-primary-lt">{inventory.total_items || 0} item</span>
                  </div>
                </div>
                <div className="card-body">
                  <ChartState loading={isLoading} empty={!inventoryValues.some((value) => value > 0)}>
                    <Chart options={inventoryOptions} series={inventoryValues} type="donut" height={330} />
                  </ChartState>
                </div>
              </div>
            </div> */}

                {activeScope === "products" ? (
                  <ProductAnalysis
                    data={overview?.product_analysis}
                    loading={isLoading}
                    rangeLabel={rangeLabel}
                    onVariantDetail={openSoldProductDetail}
                  />
                ) : null}

                {activeScope === "sales" ? (
                  <div className="col-12 dashboard-scope-content">
                    <div className="card dashboard-card dashboard-chart-card">
                      <div className="card-header">
                        <div>
                          <h3 className="card-title">Pelanggan dan pembayaran</h3>
                        </div>
                      </div>
                      <div className="card-body border-bottom dashboard-sales-support">
                        <div className="row g-3">
                          <div className="col-6 col-lg-3">
                            <div className="dashboard-loyalty-metric">
                              <span>Repeat Customer</span>
                              <strong>{repeatCustomerCount.toLocaleString("id-ID")}</strong>
                            </div>
                          </div>
                          <div className="col-6 col-lg-3">
                            <div className="dashboard-loyalty-metric">
                              <span>Repeat Rate</span>
                              <strong>{repeatCustomerRate.toLocaleString("id-ID")}%</strong>
                            </div>
                          </div>
                          <div className="col-6 col-lg-3">
                            <div className="dashboard-loyalty-metric">
                              <span>Customer Aktif</span>
                              <strong>{activeCustomerCount.toLocaleString("id-ID")}</strong>
                            </div>
                          </div>
                          <div className="col-6 col-lg-3">
                            <div className="dashboard-loyalty-metric">
                              <span>Customer Baru</span>
                              <strong>{Number(newCustomersTotal || 0).toLocaleString("id-ID")}</strong>
                              <small>{formatPercent(newCustomerRate)} dari total customer</small>
                            </div>
                          </div>
                        </div>

                        <div className="dashboard-payment-breakdown">
                          <div className="dashboard-payment-heading">
                            <span>Komposisi pembayaran</span>
                            <small>Berdasarkan nilai transaksi</small>
                          </div>
                          <div className="dashboard-payment-list">
                            {paymentMethods.length ? (
                              paymentMethods.slice(0, 4).map((method) => {
                                const paymentShare = revenue > 0 ? (Number(method.total || 0) / revenue) * 100 : 0;

                                return (
                                  <div className="dashboard-payment-item" key={method.method}>
                                    <div>
                                      <strong>
                                        {PAYMENT_METHOD_LABELS[method.method] || method.method || "Lainnya"}
                                      </strong>
                                      <span>{Number(method.transactions || 0).toLocaleString("id-ID")} transaksi</span>
                                    </div>
                                    <div className="dashboard-payment-value">
                                      <strong>{formatIDR(method.total || 0)}</strong>
                                      <span>{formatPercent(paymentShare)}</span>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="dashboard-payment-empty">Belum ada pembayaran pada periode ini.</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-vcenter card-table">
                          <thead>
                            <tr>
                              <th>Customer</th>
                              <th className="text-center">Transaksi</th>
                              <th className="text-center">Qty Produk</th>
                              <th className="text-end">Total Pembelian</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              [...Array(5)].map((_, index) => (
                                <tr key={`top-customer-loading-${index}`}>
                                  {[...Array(4)].map((__, cell) => (
                                    <td key={cell}>
                                      <div className="placeholder placeholder-lg w-75"></div>
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : topRepeatCustomers.length ? (
                              topRepeatCustomers.map((customer, index) => (
                                <tr key={`${customer.customer_id}-${index}`}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <UserAvatar
                                        fullName={customer.customer_name}
                                        imageUrl={customer.image}
                                        image_path={customer.image}
                                        size="md"
                                        className="me-2"
                                        justPhotoView={false}
                                      />
                                      <div>
                                        <div className="fw-bold">{customer.customer_name}</div>
                                        <div className="text-secondary small">{customer.phone || "Customer umum"}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center fw-bold">
                                    {Number(customer.transaction_count || 0).toLocaleString("id-ID")}
                                  </td>
                                  <td className="text-center">
                                    {Number(customer.total_qty || 0).toLocaleString("id-ID")}
                                  </td>
                                  <td className="text-end fw-bold text-primary">
                                    {formatIDR(customer.total_spent || 0)}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" className="text-center text-secondary py-5">
                                  Belum ada repeat customer pada periode ini.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : null}

                {activeScope === "inventory" ? (
                  <div className="col-12 dashboard-scope-content">
                    <MaterialUsageChart
                      data={materialUsage}
                      loading={materialUsageLoading}
                      error={materialUsageError}
                      filters={materialUsageFilters}
                      rangeLabel={rangeLabel}
                      chartColors={chartColors}
                      onFilterChange={handleMaterialUsageFilterChange}
                      onDrillDown={(category) =>
                        setMaterialUsageFilters((current) => ({
                          ...current,
                          drillDownCategory: category,
                        }))
                      }
                      onClearDrillDown={() =>
                        setMaterialUsageFilters((current) => ({
                          ...current,
                          drillDownCategory: "",
                        }))
                      }
                    />
                  </div>
                ) : null}

                {activeScope === "purchases" ? (
                  <div className="col-12 dashboard-scope-content">
                    <div className="card dashboard-card dashboard-cost-alert-card">
                      <div className="card-header">
                        <div>
                          <h3 className="card-title">Cost & HPP Alert</h3>
                          <p className="text-secondary m-0">
                            Pantau kenaikan harga bahan, pembelian tidak normal, dan potensi dampak ke HPP.
                          </p>
                        </div>
                        <div className="card-actions">
                          <span className={`badge ${costAlerts.length ? "bg-warning-lt" : "bg-success-lt"}`}>
                            {costAlerts.length ? `${costAlerts.length} alert` : "Normal"}
                          </span>
                        </div>
                      </div>
                      <div className="card-body">
                        {isLoading ? (
                          <div className="row g-3">
                            {[...Array(3)].map((_, index) => (
                              <div className="col-lg-4" key={`cost-alert-loading-${index}`}>
                                <div className="dashboard-cost-alert dashboard-cost-alert-loading">
                                  <div className="placeholder placeholder-lg w-25 mb-3"></div>
                                  <div className="placeholder placeholder-lg w-75 mb-2"></div>
                                  <div className="placeholder placeholder-sm w-100 mb-2"></div>
                                  <div className="placeholder placeholder-sm w-50"></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : costAlerts.length ? (
                          <>
                            {highestCostAlert ? (
                              <div className="alert alert-warning bg-warning-lt border-0 mb-3">
                                <div className="d-flex align-items-start gap-2">
                                  <span className="fw-bold">Insight:</span>
                                  <span>
                                    {highestCostAlert.message ||
                                      `${highestCostAlert.material_name} berubah ${formatPercent(
                                        highestCostAlert.change_percent,
                                      )} dibanding pembelian sebelumnya.`}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                            <div className="row g-3">
                              {costAlerts.map((alert, index) => {
                                const meta = getCostAlertMeta(alert);
                                const hppSample = alert?.hpp_impact?.sample_products?.[0];

                                return (
                                  <div
                                    className="col-lg-4 col-md-6"
                                    key={`${alert.type}-${alert.material_id}-${index}`}>
                                    <div
                                      className={`dashboard-cost-alert dashboard-cost-alert-${alert.severity || "low"} ${meta.toneClass}`}>
                                      <div className="dashboard-cost-alert-top">
                                        <span className="dashboard-cost-alert-icon">{meta.icon}</span>
                                        <span className="dashboard-cost-alert-type">{meta.title}</span>
                                        <strong>
                                          {meta.directionLabel} {formatPercent(Math.abs(alert.change_percent))}
                                        </strong>
                                      </div>
                                      <h4>{alert.material_name}</h4>
                                      <div className="dashboard-cost-alert-flow mt-2">
                                        <span>
                                          {meta.isPriceAlert
                                            ? formatUnitPrice(alert.previous_value, alert.unit_label)
                                            : formatIDR(alert.previous_value)}
                                        </span>
                                        <i>→</i>
                                        <span>
                                          {meta.isPriceAlert
                                            ? formatUnitPrice(alert.current_value, alert.unit_label)
                                            : formatIDR(alert.current_value)}
                                        </span>
                                      </div>
                                      <p className="f-6 text-muted mt-1">{alert.message}</p>

                                      {hppSample ? (
                                        <div className="dashboard-cost-alert-impact">
                                          HPP {hppSample.product_name} berpotensi naik{" "}
                                          {formatIDR(hppSample.estimated_delta)}
                                          /produk
                                          {alert.hpp_impact.product_count > 1
                                            ? ` dan ${alert.hpp_impact.product_count - 1} produk lain.`
                                            : "."}
                                        </div>
                                      ) : (
                                        <div className="dashboard-cost-alert-impact">{alert.description}</div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          <div className="dashboard-cost-alert-empty">
                            <span className="dashboard-cost-alert-empty-icon">✓</span>
                            <div>
                              <h4>Biaya pembelian masih normal</h4>
                              <p className="text-secondary mb-0">
                                Belum ada kenaikan harga material atau pembelian yang menonjol pada periode ini.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                {activeScope === "inventory" ? (
                  <div className="col-12 dashboard-scope-content">
                    <div className="card dashboard-card dashboard-chart-card">
                      <div className="card-header">
                        <div>
                          <h3 className="card-title">Peringatan Stok Kritis</h3>
                          <p className="text-secondary m-0">
                            {criticalStockType === "material" ? "Material" : "Produk"} yang sudah mencapai batas
                            minimum.
                          </p>
                        </div>
                        <div className="card-actions">
                          <div className="btn-group dashboard-critical-filter">
                            <button
                              type="button"
                              className={`btn btn-sm ${criticalStockType === "material" ? "btn-primary" : "btn-ghost-primary"}`}
                              onClick={() => setCriticalStockType("material")}>
                              Material
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${criticalStockType === "product" ? "btn-primary" : "btn-ghost-primary"}`}
                              onClick={() => setCriticalStockType("product")}>
                              Produk
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-vcenter card-table">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Tipe</th>
                              <th className="text-center">Stok Saat Ini</th>
                              <th className="text-center">Minimum</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              [...Array(4)].map((_, index) => (
                                <tr key={index}>
                                  {[...Array(5)].map((__, cell) => (
                                    <td key={cell}>
                                      <div className="placeholder placeholder-lg w-75"></div>
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : filteredCriticalItems.length ? (
                              filteredCriticalItems.map((item) => (
                                <tr key={`${item.item_type}-${item.id}`}>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <ItemAvatar name={item.name} image={item?.image_path || item?.image} size="sm" />
                                      <span className="fw-bold">{item.name}</span>
                                    </div>
                                  </td>
                                  <td className="text-capitalize">
                                    {item.item_type === "product" ? "Produk" : "Material"}
                                  </td>
                                  <td className="text-center">
                                    {item.stock} {item.unit}
                                  </td>
                                  <td className="text-center">
                                    {item.min_stock} {item.unit}
                                  </td>
                                  <td>
                                    <span className={`badge ${item.stock <= 0 ? "bg-danger-lt" : "bg-warning-lt"}`}>
                                      {item.stock <= 0 ? "Habis" : "Stok Rendah"}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center text-secondary py-5">
                                  Semua stok {criticalStockType === "material" ? "material" : "produk"} berada dalam
                                  kondisi aman.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : null}

                {activeScope === "finance" ? (
                  <div className="col-12 dashboard-scope-content">
                    <div className="card dashboard-card dashboard-finance-bridge-card">
                      <div className="card-header">
                        <div>
                          <span className="dashboard-table-eyebrow">REKONSILIASI LABA</span>
                          <h3 className="card-title">Dari total penjualan menjadi laba bersih tercatat</h3>
                          <p className="text-secondary m-0">
                            Biaya yang dibatalkan tidak dihitung. Histori pembatalan tetap tersimpan di Kas & Bank.
                          </p>
                        </div>
                        {meta.can_view_cash ? (
                          <Link className="btn btn-primary btn-sm" to={PATH_URL.CASH_BANK}>
                            Buka Kas & Bank
                          </Link>
                        ) : null}
                      </div>
                      <div className="card-body">
                        <ChartState loading={isLoading} empty={false}>
                          <div className="dashboard-finance-flow">
                            <div className="dashboard-finance-flow-item">
                              <span>Total Penjualan</span>
                              <strong>{formatIDR(revenue)}</strong>
                              <small>Nilai penjualan yang valid</small>
                            </div>
                            <i aria-hidden="true">−</i>
                            <div className="dashboard-finance-flow-item">
                              <span>HPP historis</span>
                              <strong>{formatIDR(summary.cogs || 0)}</strong>
                              <small>Biaya produk saat terjual</small>
                              <button
                                type="button"
                                className="dashboard-detail-link"
                                disabled={isLoading}
                                onClick={() => openProfitDetail("cogs")}>
                                Lihat rincian →
                              </button>
                            </div>
                            <i aria-hidden="true">=</i>
                            <div className="dashboard-finance-flow-item is-highlight">
                              <span>Laba kotor</span>
                              <strong>{formatIDR(grossProfit)}</strong>
                              <small>Margin {formatPercent(grossMargin)}</small>
                            </div>
                            <i aria-hidden="true">−</i>
                            <div className="dashboard-finance-flow-item">
                              <span>Biaya operasional</span>
                              <strong>{formatIDR(operatingExpenses)}</strong>
                              <small>Biaya aktif, termasuk penyusutan</small>
                              <div className="dashboard-cost-detail-links">
                                <button
                                  type="button"
                                  className="dashboard-detail-link"
                                  disabled={isLoading || !meta.can_view_expenses}
                                  onClick={() => openProfitDetail("operating_costs")}>
                                  Operasional:{" "}
                                  {formatIDR(
                                    summary.operating_costs ??
                                      operatingExpenses - Number(summary.depreciation_expense || 0),
                                  )}{" "}
                                  →
                                </button>
                                <button
                                  type="button"
                                  className="dashboard-detail-link"
                                  disabled={isLoading || !meta.can_view_depreciation}
                                  onClick={() => openProfitDetail("depreciation")}>
                                  Penyusutan: {formatIDR(summary.depreciation_expense || 0)} →
                                </button>
                              </div>
                            </div>
                            <i aria-hidden="true">=</i>
                            <div className="dashboard-finance-flow-item is-result">
                              <span>Laba bersih tercatat</span>
                              <strong>{formatIDR(netProfit)}</strong>
                              <small>Margin {formatPercent(netMargin)}</small>
                            </div>
                          </div>
                          <div className="dashboard-finance-note">
                            <span>i</span>
                            <p>
                              Kas bersih penjualan periode ini{" "}
                              <strong>
                                {summary.sales_cash_net == null ? "—" : formatIDR(summary.sales_cash_net)}
                              </strong>
                              , berdasarkan tanggal mutasi kas termasuk pengembalian. Pembayaran tercatat untuk invoice
                              periode ini <strong>{formatIDR(revenueCollected)}</strong>, dengan sisa piutang{" "}
                              <strong>{formatIDR(revenueReceivables)}</strong>. Laba bersih tercatat hanya mencakup
                              biaya yang sudah dimasukkan ke AKAS.
                            </p>
                          </div>
                        </ChartState>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
      <CashierSummaryModal
        summary={overview?.meta?.start_date === dateRange.startDate && overview?.meta?.end_date === dateRange.endDate && !error
          ? { cashier_summary: overview?.cashier_summary } : null}
        dateRange={dateRange}
        loading={isLoading}
        onRetry={() => fetchDashboardOverview(dateRange.startDate, dateRange.endDate, groupBy)}
        scopeNote="Mengikuti periode Dashboard. Draft dan transaksi dibatalkan tidak dihitung."
      />
      <ProfitDetailModal
        type={profitDetailType}
        detail={profitDetail}
        loading={profitDetailLoading}
        error={profitDetailError}
        rangeLabel={rangeLabel}
        onClose={closeProfitDetail}
        onPageChange={changeProfitDetailPage}
        onRetry={() =>
          fetchDashboardProfitDetails(profitDetailType, dateRange.startDate, dateRange.endDate, profitDetailPage)
        }
      />
      <SoldProductDetailModal
        product={selectedSoldProduct}
        detail={soldProductDetail}
        loading={soldProductDetailLoading}
        error={soldProductDetailError}
        rangeLabel={rangeLabel}
        onClose={closeSoldProductDetail}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userProfile: state.auth?.login?.profile,
  profitDetail: state?.dashboard?.profitDetail,
  profitDetailLoading: state?.dashboard?.profitDetailLoading,
  profitDetailError: state?.dashboard?.profitDetailError,
  overview: state?.dashboard?.overview,
  isLoading: state?.dashboard?.loading || false,
  error: state?.dashboard?.error,
  storeSetting: state?.stores?.setting,
  soldProductDetail: state?.dashboard?.soldProductDetail,
  soldProductDetailLoading: state?.dashboard?.soldProductDetailLoading || false,
  soldProductDetailError: state?.dashboard?.soldProductDetailError,
  materialUsage: state?.dashboard?.materialUsage,
  materialUsageLoading: state?.dashboard?.materialUsageLoading || false,
  materialUsageError: state?.dashboard?.materialUsageError,
});

export default connect(mapStateToProps, {
  fetchDashboardProfitDetails,
  fetchDashboardOverview,
  fetchDashboardMaterialUsage,
  fetchSoldProductDetail,
  clearSoldProductDetail,
})(DashboardPage);
